import { create } from "zustand";
import { api } from "../lib/api";

export interface UserAIKey {
  provider: string;
  keyHint: string;
  createdAt: string;
}

interface UserAIKeysState {
  keys: UserAIKey[];
  loading: boolean;
  saving: string | null;
  error: string | null;

  fetchKeys: () => Promise<void>;
  saveKey: (provider: string, apiKey: string) => Promise<void>;
  deleteKey: (provider: string) => Promise<void>;
}

export const useUserAIKeysStore = create<UserAIKeysState>()((set) => ({
  keys: [],
  loading: false,
  saving: null,
  error: null,

  fetchKeys: async () => {
    set({ loading: true, error: null });
    try {
      const data = await api.get<{ keys: UserAIKey[] }>("/api/v1/user-keys");
      set({
        keys: (data.keys ?? []).map((k) => ({
          provider: k.provider,
          keyHint: k.keyHint ?? (k as unknown as Record<string, string>).key_hint ?? "",
          createdAt: k.createdAt ?? (k as unknown as Record<string, string>).created_at ?? "",
        })),
        loading: false,
      });
    } catch {
      set({ keys: [], loading: false });
    }
  },

  saveKey: async (provider: string, apiKey: string) => {
    set({ saving: provider, error: null });
    try {
      const data = await api.post<UserAIKey>("/api/v1/user-keys", {
        provider,
        api_key: apiKey,
      });
      const newKey: UserAIKey = {
        provider: data.provider,
        keyHint: data.keyHint ?? (data as unknown as Record<string, string>).key_hint ?? "",
        createdAt: data.createdAt ?? (data as unknown as Record<string, string>).created_at ?? "",
      };
      set((s) => ({
        keys: [...s.keys.filter((k) => k.provider !== provider), newKey],
        saving: null,
      }));
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Failed to save key";
      set({ saving: null, error: msg });
      throw err;
    }
  },

  deleteKey: async (provider: string) => {
    try {
      await api.delete(`/api/v1/user-keys/${provider}`);
      set((s) => ({
        keys: s.keys.filter((k) => k.provider !== provider),
      }));
    } catch {
      // silently fail
    }
  },
}));
